import SlimSelect from 'slim-select';

/**
 * Hook into Alpine so that it is available no matter at which point Alpine loads.
 */
document.addEventListener('alpine:init', () => {
    /**
     * Custom data object to pass into AlpineJS for select box functionality.
     *
     * @param string id       identifier attribute for this select box
     * @param bool   search   true if this select box should support search
     * @param bool   custom   true if custom values are allowed to be added
     * @param array  language list of language translations for select dropdowns
     * @param string length   maximum length for custom entries
     */
    Alpine.data('select', (id, value, search, custom, language, length) => ({
        /**
         * @var string identifier attribute for this select box
         */
        id: id,

        /**
         * @var string selected value
         */
        value: value,

        /**
         * @var bool true if this select box should support search, required for custom
         */
        search: search || custom,

        /**
         * @var bool true if custom values are allowed to be added
         */
        custom: custom,

        /**
         * @var array list of language translations for select dropdowns
         */
        language: language,

        /**
         * @var object reference to the slim-select library object
         */
        slim: null,

        /**
         * @var bool true when a selection has been made
         */
        hasSelection: false,

        /**
         * @var string maximum length for custom entries
         */
        length: length,

        /**
         * Initialize the dropdown.
         *
         * @return void
         */
        construct() {
            this.updateHtmlValueElementByRef()
            this.initSlim();
            this.updateSelectionByRef();

            Livewire.hook('commit', ({ succeed }) => succeed(
                () => queueMicrotask(() => this.refreshSlim())
            ));

            Livewire.on(`refreshSelect${this.id}`, (data) => {
                this.refreshSlim(true);
                this.slim.setData(data.options);
            });
        },

        /**
         * Update the value for HTML element for case insensitive.
         *
         * @return void
         */
        updateHtmlValueElementByRef() {
            if (this.value && this.value.length) {
                const selectElement = this.$refs.select;
                const selectedValue = this.value.toLowerCase();

                for (const option of selectElement.options) {
                    if (option.value.toLowerCase() === selectedValue) {
                        selectElement.value = option.value;
                        break;
                    }
                }
            }
        },

        /**
         * Initialize the slim-select plugin.
         *
         * @return void
         */
        initSlim() {
            if (this.isInitialized(this.id)) {
                return;
            }

            let options = {
                select:              '#'+this.id,
                settings: {
                    showSearch:          this.search,
                    allowDeselect:       true,
                    searchPlaceholder:   this.language.search_only,
                    searchText:          this.language.results_none,
                    contentLocation:    this.$root.querySelector('.select-plugin'),
                },
                events: {
                    beforeOpen:          () => this.$root.querySelector('.select-plugin').classList.add('select-open'),
                    beforeClose:         () => this.$root.querySelector('.select-plugin').classList.remove('select-open'),
                }
            };

            if (this.custom) {
                options.events.addable           = () => this.addAction();
                options.settings.searchPlaceholder = this.language.search_custom;
                options.settings.searchText        = this.language.results_custom;
            }

            this.slim = new SlimSelect(options);

            this.$root.querySelector('.ss-list').setAttribute('tabIndex', -1);

            // the base library does not trigger searches on mouse or touch paste events
            if (this.search) {
                this.$root.querySelector('.ss-search').classList.add('search-enabled');
                this.$root.querySelector('.ss-search input').setAttribute('x-on:input', 'searchAction()');

                // the base library does not support the enter key on custom options
                // or a maximum length that must be appended to the search box
                if (this.custom) {
                    this.$root.querySelector('.ss-search').classList.add('custom-enabled');
                    this.$root.querySelector('.ss-search input').setAttribute('x-on:keyup.enter', 'enterAction');

                    if (length) {
                        this.$root.querySelector('.ss-search input').setAttribute('maxlength', this.length);
                    }
                }
            }
        },

        /**
         * Check does the element of select exists in the DOM.
         *
         * @param id
         * @return {boolean}
         */
        hasElementInDom(id) {
            return Boolean(document.getElementById(id));
        },

        /**
         * Check does the element with ID has instance of the Slim Select.
         *
         * @param {String} id
         * @return {boolean}
         */
        isInitialized(id) {
            const element = document.getElementById(id);

            return element && this.isElementInitialized(element);
        },

        /**
         * Check does the element has instance of the Slim Select.
         *
         * @param {HTMLElement} element
         * @return {boolean}
         */
        isElementInitialized(element) {
            return element.hasAttribute('data-id');
        },

        /**
         * Search the select dropdown for the value entered in the search box.
         *
         * @return void
         */
        searchAction() {
            let value = this.slim.render.content.search.input.value;
            this.slim.search(value);

            let highlighted = this.$root.querySelectorAll('.ss-option.ss-highlighted');
            let options     = this.$root.querySelectorAll('.ss-option:not(.ss-disabled)');

            if (highlighted.length) {
                highlighted.forEach((option) => option.classList.remove('ss-highlighted'));
            }

            if (options.length) {
                options[0].classList.add('ss-highlighted');
            }
        },

        /**
         * What happens when the user presses enter in the select dropdown search.
         *
         * Only add an entry when there are no search results.
         *
         * @return void
         */
        enterAction() {
            if (this.custom) {
                let options = this.$root.querySelectorAll('.ss-option:not(.ss-disabled)');

                if (!options.length) {
                    this.$root.querySelector('.ss-addable').click();
                }
            }
        },

        /**
         * Action to perform when pressing the add button on the search box.
         *
         * We want to prevent adding an option that already exists in the dropdown.
         *
         * @return void
         */
        addAction() {
            let action = false;
            let value  = this.$root.querySelector('.ss-search input').value;

            if (value.trim() !== '') {
                let found = false;

                this.$refs.select.querySelectorAll('option').forEach((option) => {
                    if (option.value.toLowerCase().trim() === value.toLowerCase().trim()) {
                        value = option.value;
                        found = true;
                        return;
                    }
                });

                if (found) {
                    this.slim.set(value);
                } else {
                    action = {
                        text: format.htmlspecialchars(value),
                        value: value
                    };
                }
            }

            return action;
        },

        /**
         * Set whether this select dropdown has a selection or not.
         *
         * @return void
         */
        updateSelectionByRef() {
            this.updateSelectionByValue(this.$refs.select.value);
        },

        /**
         * Refresh the slim select plugin if it ever becomes out of sync with livewire.
         *
         * This can happen because we do not want javascript plugins reinitializing
         * every time livewire redraws the page because of extreme slowdown.
         * Therefore, we wait until an update happens, check to see if the
         * dropdown became out of sync, and only then reinitialize. An instance
         * of this would be cancelling a form, which sends a signal to livewire
         * to reset the values, which will update the select dropdown but not
         * reinitialize the javascript wrapper plugin.
         *
         * Setting the value would be a more efficient alternative, however
         * validation runs on every update and that means by setting the value
         * it would run validation on the field and cause any dropdowns that
         * are required to show an error even though the user never actually
         * edited the field.
         *
         * @param force allow refresh without the check of selected value.
         *
         * @return void
         */
        refreshSlim(force = false) {
            if (!('select' in this.$refs)) {
                return;
            }

            if (this.slim.settings.isOpen) {
                return;
            }

            if (force ||  -1 === this.slim.getSelected().indexOf(this.$refs.select.value)) {
                this.slim.destroy();
                if (!this.hasElementInDom(this.id)) {
                    return;
                }
                this.updateHtmlValueElementByRef();
                this.initSlim();
                this.updateSelectionByRef();
            }
        },

        updateSelectionByValue(value) {
            this.hasSelection = value !== '';
        },


        /**
         * Toggle the menu of the select dropdown open or closed based on its current status.
         *
         * @return void
         */
        toggle() {
            if (this.$root.querySelector('.select-plugin').classList.contains('select-open')) {
                this.slim.close();
            } else {
                this.slim.open();
            }
        },
    }));
});
